import useMobileDetect from "@/hooks/useMobileDetect";
import {
  AutoComplete,
  AutoCompleteProps,
  Divider,
  Drawer,
  Form,
  Input,
  message as messageToast,
  Modal,
} from "antd";
import { CloseSmall } from "@icon-park/react";
import Coupon from "@/app/assets/user/Coupon.svg";
import CouponPc from "@/app/assets/common/CouponPc.svg";
import Delivery from "@/app/assets/user/Delivery.svg";
import DeliveryPc from "@/app/assets/common/DeliveryPc.svg";
import Shiled from "@/app/assets/user/Shiled.svg";
import ShiledPc from "@/app/assets/common/ShiledPc.svg";
import OtpInput from "react-otp-input";
import Image from "next/image";
import Bravve from "@/app/assets/common/Bravve.png";
import BravvePc from "@/app/assets/common/BravvePc.png";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import { useEffect, useRef, useState } from "react";
import useSWR from "swr";
import { EMAIL_SUFFIX } from "@/static/locales";
import EyeOn from "@/app/assets/user/EyeOn.svg";
import EyeOff from "@/app/assets/user/EyeOff.svg";
import KeyHole from "@/app/assets/user/KeyHole.svg";
import {
  login,
  register,
  resetPassword,
  smsCode,
  queryUserSigned,
  googleLogin,
  facebookLogin,
  verifyEmailThirdLogin,
  thirdUserBindEmail,
} from "@/service/user";
import { Countdown, SliderVerification } from "../common";
import { CountdownRef } from "../common/CountDown";
import {
  Captcha,
  Event_Name,
  Fail_Reason,
  Login_Close,
  Login_Submit_Method,
  LoginType,
  Submit_Status,
  ThirdLogin,
  VerifyType,
} from "@/service/user/enum";
import userInfoStore from "@/store/userInfoStore";
import { queryShopingCartNum } from "@/service/carts";
import shopCartStore from "@/store/shopCartStore";
import { addGtag, desensitizeEmail, isEmail } from "@/utils/tools";
import { PrivacyPolicyPagesEnum } from "@/utils/const";
import { openPricacyPolicy } from "@/utils/tools";
import { getShippingFeeConf } from "@/service/common/index";
import { Numeric } from "@/app/components/common/Numeric";
import { StatusCode } from "@/utils/statusCode";
import { Storage } from "@/utils/storage";
import Succeed from "@/app/assets/common/Succeed.svg";
import { BravveSmartButton } from "../common/BravveSmartButton";
import { useGoogleLogin } from "@react-oauth/google";
import { Coupon as CouponCom } from "@/app/components/activity/Coupon";

import "swiper/css";
import "swiper/css/pagination";
import "./style/LoginCarousel.css";
import "./style/LoginForm.css";

export type LoginProps = {
  open: boolean;
  onClose: () => void;
};

type FieldType = {
  email?: string;
  code?: string;
  password?: string;
};

let initialValues = {
  email: "",
  code: "",
  password: "",
};

export const Login: React.FC<LoginProps> = ({ open, onClose, ...props }) => {
  const isMobile = useMobileDetect();
  const countDownRef = useRef<CountdownRef>(null);
  const thirdLoginToken = useRef<string>();
  const formRefValue = useRef(initialValues);
  const [form] = Form.useForm();
  const watchEmail = Form.useWatch("email", form);
  const watchCode = Form.useWatch("code", form);
  const watchPassword = Form.useWatch("password", form);
  const [options, setOptions] = useState<AutoCompleteProps["options"]>([]);
  const [step, setStep] = useState<number>(1);
  const [otp, setOtp] = useState("");
  const [openSlider, setOpenSlider] = useState(false);
  const [verifyType, setVerifyType] = useState(VerifyType.SIGN_UP);
  const [resendStatus, setResendStatus] = useState(false);
  const [isThirdLogin, setThirdLogin] = useState(false);
  const [isBindGoogle, setBindGoogle] = useState(true);
  // const [showFacebook, setShowFacebook] = useState(false);
  const [openCoupon, setOpenCoupon] = useState(false);
  const { setUserInfo } = userInfoStore();
  const { setShopCart } = shopCartStore();
  const [carouselArr, setCarouselArr] = useState<
    { icon: JSX.Element; title: string; des: JSX.Element | string }[]
  >([
    {
      icon: isMobile ? <Coupon width={54} /> : <CouponPc width={54} />,
      title: `${process.env.NEXT_PUBLIC_discount} Discount`,
      des: "Discount when you first register",
    },
    {
      icon: isMobile ? <Delivery width={54} /> : <DeliveryPc width={54} />,
      title: "Delivery Guarantee",
      des: "Free Shipping on orders over $",
    },
    {
      icon: isMobile ? <Shiled width={54} /> : <ShiledPc width={54} />,
      title: "Information Security",
      des: "All information security protection",
    },
  ]);

  const { data } = useSWR(
    ["getShippingFeeConf"],
    async () => {
      const { data } = await getShippingFeeConf<{ totalAmount: number }>({
        country: "USA",
      });
      return data;
    },
    { revalidateOnFocus: false },
  );

  useEffect(() => {
    setCarouselArr([
      {
        icon: isMobile ? <Coupon width={54} /> : <CouponPc width={54} />,
        title: `${process.env.NEXT_PUBLIC_discount} Discount`,
        des: "Discount when you first register",
      },
      {
        icon: isMobile ? <Delivery width={54} /> : <DeliveryPc width={54} />,
        title: "Delivery Guarantee",
        des: "Free Shipping on orders over $",
      },
      {
        icon: isMobile ? <Shiled width={54} /> : <ShiledPc width={54} />,
        title: "Information Security",
        des: "All information security protection",
      },
    ]);
  }, [isMobile]);

  useEffect(() => {
    if (data) {
      carouselArr[1].des = (
        <span>
          Free Shipping on orders over{" "}
          <Numeric
            className={`${isMobile ? "text-bravve-100" : "text-bravve-300"}`}
            value={data?.totalAmount}
          />
        </span>
      );

      setCarouselArr([...carouselArr]);
    }
  }, [data, isMobile]);

  useEffect(() => {
    if (step === 2 || step === 4 || step === 5) {
      countDownRef.current?.startCountdown();
    }
  }, [step]);

  useEffect(() => {
    return () => {
      setThirdLogin(false);
      setBindGoogle(true);
    };
  }, []);

  useEffect(() => {
    Storage.setItem("SlideVerifyInfo", { accountUuid: "", xPos: "" });
  }, []);

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: (codeResponse) => callbackGoogleLogin(codeResponse),
    prompt: "",
  });

  const callbackGoogleLogin = async (response: { access_token: string }) => {
    const access_token = response.access_token;
    const { status, data, message } = await googleLogin(access_token);

    let gtagObj = {
      oauth_result: ThirdLogin.Google,
      status: status === 200 ? Submit_Status.success : Submit_Status.fail,
    } as {
      oauth_result: ThirdLogin;
      status: Submit_Status;
      fail_reason?: string;
    };

    if (status !== 200) {
      gtagObj.fail_reason = message;
    }

    addGtag(Event_Name.oauth_result, gtagObj);

    if (status === 200) {
      setStep(1);
      onClose();
      setUserInfo(data);
      onLoginSuccess(data.id);
      refreshShopCart();

      if (data?.signupFlag) {
        setOpenCoupon(true);
      }
    } else {
      messageToast.error(message);
    }
  };

  // useEffect(() => {
  //   // 加载 Facebook SDK
  //   (window as any).fbAsyncInit = function () {
  //     (window as any).FB.init({
  //       appId: process.env.NEXT_PUBLIC_Facebook_App_ID, // 替换为你的 Facebook 应用 ID
  //       cookie: true,
  //       xfbml: true,
  //       version: "v22.0",
  //     });
  //   };

  //   const loadFacebookScript = () => {
  //     const script = document.createElement("script");
  //     script.id = "fb-sdk";
  //     script.src = process.env.NEXT_PUBLIC_Facebook_SDK_URL as string;
  //     // script.async = true;
  //     script.defer = true;
  //     script.crossOrigin = "anonymous";
  //     document.body.append(script);
  //   };
  //   setTimeout(() => {
  //     loadFacebookScript();
  //   }, 300);
  // }, []);

  const callbackFacebookLogin = async (token: string) => {
    const { status, data, message } = await facebookLogin(token);

    let gtagObj = {
      oauth_result: ThirdLogin.Facebook,
      status: status === 200 ? Submit_Status.success : Submit_Status.fail,
    } as {
      oauth_result: ThirdLogin;
      status: Submit_Status;
      fail_reason?: string;
    };

    if (status !== 200) {
      gtagObj.fail_reason = message;
    }

    addGtag(Event_Name.oauth_result, gtagObj);

    if (status === 200) {
      setStep(1);
      onClose();
      setUserInfo(data);
      onLoginSuccess(data.id);
      refreshShopCart();

      if (data?.signupFlag) {
        setOpenCoupon(true);
      }
    } else if (status === StatusCode.NoEmail) {
      thirdLoginToken.current = data;
      setThirdLogin(true);
      setBindGoogle(false);
      setStep(6);
    } else {
      messageToast.error(message);
    }
  };

  // const haneleFacebookLogin = () => {
  //   addGtag(Event_Name.oauth_attempt, {
  //     oauth_provider: ThirdLogin.Facebook,
  //   });

  //   (window as any).FB.login(
  //     (response: any) => {
  //       if (response.authResponse) {
  //         const { accessToken } = response.authResponse;
  //         callbackFacebookLogin(accessToken);
  //       } else {
  //         addGtag(Event_Name.oauth_result, {
  //           oauth_result: ThirdLogin.Facebook,
  //           status: Submit_Status.fail,
  //           fail_reason: Fail_Reason.user_cancelled,
  //         });
  //       }
  //     },
  //     { scope: "public_profile,email" }, // 请求用户基本信息和邮箱权限
  //   );
  // };

  const handleGta = () => {
    let stage = Login_Close.begin;

    if (step === 1) {
      stage = Login_Close.begin;
    } else if (step === 2) {
      stage = Login_Close.register;
    } else if (step === 3 || step === 4) {
      stage = Login_Close.password_login;
    } else if (step === 5) {
      stage = Login_Close.code_login;
    } else if (step === 6 || step === 7 || step === 8) {
      stage = Login_Close.third_party_auth;
    }

    addGtag(Event_Name.login_close, {
      close_stage: stage,
    });
  };

  const _renderThirdLogin = () => {
    return (
      <>
        <div
          className="flex items-center justify-center"
          style={{ width: "100%" }}
        >
          <Divider
            style={{
              minWidth: "auto",
              width: "100%",
              marginTop: 28,
              marginBottom: 28,
            }}
          />{" "}
          <span className="mx-12 text-lg leading-24 text-bravve-sub-100">
            or
          </span>{" "}
          <Divider
            style={{
              minWidth: "auto",
              width: "100%",
              marginTop: 28,
              marginBottom: 28,
            }}
          />
        </div>

        <BravveSmartButton
          time={1000}
          size="large"
          onClick={handleGoogleLogin}
          className="relative flex w-full justify-between"
        >
          <div
            style={{
              position: "absolute",
              top: "55%",
              left: 32,
              transform: "translate(-50%, -50%)",
            }}
          >
            <iconpark-icon name="google" icon-id="google" size="2.4rem" />
          </div>
          <div className="flex text-center">Continue with Google</div>
        </BravveSmartButton>

        {/* {showFacebook && (
          <BravveSmartButton
            time={1000}
            size="large"
            onClick={haneleFacebookLogin}
            className="my-16 flex w-full items-center justify-between"
          >
            <div
              style={{
                position: "absolute",
                top: "55%",
                left: 32,
                transform: "translate(-50%, -50%)",
              }}
            >
              <iconpark-icon
                name="facebook-"
                icon-id="facebook-"
                size="2.4rem"
              />
            </div>
            <span className="mx-auto">Continue with Facebook</span>
          </BravveSmartButton>
        )} */}
      </>
    );
  };

  const _renderMoblieTop = () => {
    return (
      <div className="mb-40">
        {step === 1 && (
          <>
            <div className="text-center text-2xl font-bold leading-28 text-bravve-100">
              Sign in / Register
            </div>
            <div className="mt-16 flex items-center text-success-200">
              <KeyHole width={15} fill={"#24961B"} />
              <span className="ml-6">All data will be encrypted</span>
            </div>
          </>
        )}
        {(step === 2 || step === 3 || step === 5) && (
          <>
            <div className="text-center text-2xl font-bold leading-28 text-bravve-100">
              {step === 2 ? "Create Account" : "Sign in"}
            </div>
            <div className="mt-16 text-center leading-22 text-bravve-100">
              {formRefValue.current.email}
            </div>
            <div
              className="mt-6 cursor-pointer border-b border-bravve-100 text-center leading-22 text-bravve-100"
              onClick={() => {
                setStep(1);
              }}
            >
              Use a different account
            </div>
          </>
        )}
        {step === 4 && (
          <>
            <div className="text-center text-2xl font-bold leading-28 text-bravve-100">
              Forgot Password
            </div>
            <div className="mt-16 text-center leading-22 text-bravve-100">
              We&apos;ve sent a code to {formRefValue.current.email}.
            </div>
            <div className="text-center leading-22 text-bravve-100">
              Please enter the code below.
            </div>
          </>
        )}
        {step === 6 && (
          <>
            <div className="text-center text-2xl font-bold leading-28 text-bravve-100">
              Link Account to Email
            </div>
            <div className="mt-16 text-center leading-22 text-bravve-100">
              Your account needs to be linked to
            </div>
            <div className="text-center leading-22 text-bravve-100">
              an email address
            </div>
          </>
        )}
        {step === 7 && (
          <>
            <div className="text-center text-2xl font-bold leading-28 text-bravve-100">
              Sign in
            </div>
            <div className="mt-16 text-center leading-22 text-bravve-100">
              Your email address has already been registered.
            </div>
            <div className="text-center leading-22 text-bravve-100">
              Pleaselog in with the existing account.
            </div>
          </>
        )}
        {step === 8 && (
          <>
            <div className="text-center text-2xl font-bold leading-28 text-bravve-100">
              Forgot Password
            </div>
            <div className="mt-16 text-center leading-22 text-bravve-100">
              We&apos;ve sent a code to {formRefValue.current.email}.
            </div>
            <div className="text-center leading-22 text-bravve-100">
              Please enter the code below.
            </div>
          </>
        )}
        {step === 9 && (
          <div className="text-center text-bravve-100">
            <div className="flex items-center justify-center">
              <Succeed width="56" />
            </div>
            <div className="my-16 text-2xl font-bold leading-28">
              Login Successful
            </div>
            <div className="leading-22">
              Your account is now linked to{" "}
              {isBindGoogle ? ThirdLogin.Google : ThirdLogin.Facebook}.
            </div>
            <div className="leading-22">
              In future, you can sign in using{" "}
              {isBindGoogle ? ThirdLogin.Google : ThirdLogin.Facebook} or
            </div>
            <div className="leading-22">your email and password.</div>
          </div>
        )}
      </div>
    );
  };

  const _renderMoblieCarousel = () => {
    return (
      <Swiper
        className="LoginCarousel mb-14"
        modules={[Pagination, Autoplay]}
        pagination
        style={{ width: "100%", paddingBottom: 16 }}
        loop
        observer={true}
        observeParents={true}
      >
        {carouselArr.map(({ icon, title, des }, index) => (
          <SwiperSlide key={title}>
            <div className="flex flex-col flex-wrap justify-center text-center">
              <div className="mt-16 flex justify-center">{icon}</div>
              <div className="mt-16 text-xl font-bold leading-26 text-bravve-100">
                {title}
              </div>
              <div className="mb-24 mt-2 leading-22 text-bravve-100">{des}</div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    );
  };

  const getEmails = (searchText: string) =>
    !searchText
      ? []
      : EMAIL_SUFFIX.map((item) => {
          return { value: searchText + "@" + item };
        });

  const handleVerifyEmailThirdLogin = async () => {
    const { email } = form.getFieldsValue();
    const { status, message, data } = await verifyEmailThirdLogin(
      email,
      thirdLoginToken.current,
      ThirdLogin.Facebook,
    );

    if (status === 200) {
      addGtag(Event_Name.login_email_submit, {
        user_email: desensitizeEmail(email),
      });

      setStep(1);
      onClose();
      setUserInfo(data);
      onLoginSuccess(data.id);
      refreshShopCart();
      setThirdLogin(false);
      form.resetFields();
    } else if (status === StatusCode.VerifyThirdLoginEmailError) {
      setStep(7);
    } else if (status === StatusCode.EmailBound) {
      // 邮箱重复绑定
      messageToast.error(message);
    }
  };

  const handleContinue = async () => {
    const { email } = form.getFieldsValue();
    // if (email === "111111") {
    //   setShowFacebook(true);
    //   return;
    // }

    if (!isEmail(email)) {
      messageToast.error("Please enter a valid email address");

      return;
    }

    const {
      status: signedStatus,
      data: signedData,
      message: signedMessage,
    } = await queryUserSigned({
      email,
    });

    if (signedStatus === 200) {
      addGtag(Event_Name.login_email_submit, {
        user_email: desensitizeEmail(email),
      });

      formRefValue.current = {
        ...initialValues,
        ...form.getFieldsValue(),
      };

      if (signedData.signed) {
        setStep(3);
      } else {
        const { accountUuid, xPos } = Storage.getItem("SlideVerifyInfo");
        const { status, message } =
          xPos === ""
            ? await smsCode({
                email,
                type: VerifyType.SIGN_UP,
              })
            : await smsCode({
                email,
                type: VerifyType.SIGN_UP,
                xPos,
                verifyType: getVerifyType(verifyType),
                uuid: accountUuid,
              });

        if (status === 200) {
          messageToast.success(message);
          setStep(2);
          countDownRef.current?.startCountdown();
        } else if (
          status === StatusCode.TriggerRateLimit ||
          status === StatusCode.VerificationExpired ||
          status === StatusCode.VerificationFailed
        ) {
          messageToast.error(message);
          setOpenSlider(true);
          setVerifyType(VerifyType.SIGN_UP);
        } else {
          messageToast.error(message);
        }
      }
    } else {
      messageToast.error(signedMessage);
    }
  };

  const handleRegisterOrLogin = async () => {
    const { code, password } = form.getFieldsValue();
    const { email } = formRefValue.current;

    if (!code || !password) {
      messageToast.error("Please enter the value first");

      return;
    }

    // 2:注册; 4:忘记密码
    if (step === 2) {
      const { status, message, data } = await register({
        verifyCode: code,
        authorization: btoa(`${email}:${password}`),
      });

      let gtagObj = {
        user_email: desensitizeEmail(email),
        status: status === 200 ? Submit_Status.success : Submit_Status.fail,
      } as {
        user_email: string;
        status: Submit_Status;
        fail_reason?: string;
      };

      if (status !== 200) {
        gtagObj.fail_reason = message;
      }

      addGtag(Event_Name.register_submit, gtagObj);

      if (status === 200) {
        form.resetFields();
        setStep(1);
        setUserInfo(data);
        onLoginSuccess(data.id);
        onClose();
        setOpenCoupon(true);
      } else if (status === StatusCode.TriggerRateLimit) {
        setOpenSlider(true);
        setVerifyType(VerifyType.SIGN_UP);
      } else {
        messageToast.error(message);
      }
    } else if (step === 4) {
      const { status, message, data } = await resetPassword({
        verifyCode: code,
        authorization: btoa(`${email}:${password}`),
      });

      if (status === 200) {
        form.resetFields();
        setStep(1);
        setUserInfo(data);
        onClose();
        messageToast.success(message);
      } else if (status === StatusCode.TriggerRateLimit) {
        setOpenSlider(true);
        setVerifyType(VerifyType.RESET_PASS);
      } else {
        messageToast.error(message);
      }
    }
  };

  const refreshShopCart = async () => {
    // 刷新购物车数量
    const cartData = await queryShopingCartNum();

    if (cartData?.status === 200) {
      let count = cartData?.data?.count || 0; // 返回购物车数量，默认值为0
      setShopCart({ count });
    }
  };

  const handlePWDSuccess = async (data: any) => {
    form.resetFields();
    setStep(1);
    onClose();
    setUserInfo(data);
    onLoginSuccess(data.id);
    refreshShopCart();
    Storage.setItem("SlideVerifyInfo", { accountUuid: "", xPos: "" });
  };

  const getVerifyType = (type: VerifyType) => {
    if (type === VerifyType.SIGN_UP) {
      return Captcha.REGISTER;
    } else if (type === VerifyType.LOGIN) {
      return Captcha.LOGIN;
    } else if (type === VerifyType.RESET_PASS) {
      return Captcha.UPDATE_PASSWORD;
    }
  };

  const handleSignInWithPWD = async () => {
    const { password } = form.getFieldsValue();
    const { email } = formRefValue.current;

    if (!password) {
      messageToast.error("Please enter the value first");

      return;
    }

    const { accountUuid, xPos } = Storage.getItem("SlideVerifyInfo");

    const { message, data, status } =
      xPos === ""
        ? await login({
            email,
            authorization: btoa(`${email}:${password}`),
            loginType: LoginType.PASSWORD,
          })
        : await login(
            {
              email,
              authorization: btoa(`${email}:${password}`),
              loginType: LoginType.PASSWORD,
            },
            xPos,
            getVerifyType(verifyType),
            accountUuid,
          );

    let gtagObj = {
      user_email: desensitizeEmail(email),
      method: Login_Submit_Method.password,
      status: status === 200 ? Submit_Status.success : Submit_Status.fail,
    } as {
      user_email: string;
      method: Login_Submit_Method;
      status: Submit_Status;
      fail_reason?: string;
    };

    if (status !== 200) {
      gtagObj.fail_reason = message;
    }

    addGtag(Event_Name.login_submit, gtagObj);

    if (status === 200) {
      handlePWDSuccess(data);
    } else if (
      status === StatusCode.TriggerRateLimit ||
      status === StatusCode.VerificationExpired ||
      status === StatusCode.VerificationFailed
    ) {
      messageToast.error(message);
      setOpenSlider(true);
      setVerifyType(VerifyType.LOGIN);
    } else {
      messageToast.error(message);
    }
  };

  const handleSignInWithCode = async () => {
    const { code } = form.getFieldsValue();
    const { email } = formRefValue.current;

    if (!code) {
      messageToast.error("Please enter the value first");

      return;
    }

    const { accountUuid, xPos } = Storage.getItem("SlideVerifyInfo");

    const { message, status, data } =
      xPos === ""
        ? await login({
            email,
            verifyCode: code,
            loginType: LoginType.EMAIL_CODE,
          })
        : await login(
            {
              email,
              verifyCode: code,
              loginType: LoginType.EMAIL_CODE,
            },
            xPos,
            getVerifyType(verifyType),
            accountUuid,
          );

    let gtagObj = {
      user_email: desensitizeEmail(email),
      method: Login_Submit_Method.code,
      status: status === 200 ? Submit_Status.success : Submit_Status.fail,
    } as {
      user_email: string;
      method: Login_Submit_Method;
      status: Submit_Status;
      fail_reason?: string;
    };

    if (status !== 200) {
      gtagObj.fail_reason = message;
    }

    addGtag(Event_Name.login_submit, gtagObj);

    if (status === 200) {
      handlePWDSuccess(data);
    } else if (
      status === StatusCode.TriggerRateLimit ||
      status === StatusCode.VerificationExpired ||
      status === StatusCode.VerificationFailed
    ) {
      messageToast.error(message);
      setOpenSlider(true);
      setVerifyType(VerifyType.LOGIN);
    } else {
      messageToast.error(message);
    }
  };

  // 模拟用户登录成功
  const onLoginSuccess = (userId: string) => {
    // 设置 user_id
    // @ts-ignore
    window.gtag("set", "user_id", userId);

    // 可以选择发送一个登录事件
    // @ts-ignore
    window.gtag("event", "login", {
      method: "email", // 例如 'email', 'google', 等
    });
  };

  const _renderResend = (type: VerifyType) => {
    return (
      <div className="text-center leading-22 text-bravve-sub-100">
        Didn&apos;t get a code?{" "}
        <Countdown
          ref={countDownRef}
          onClick={() => {
            setOpenSlider(true);
          }}
        >
          <span
            className="mr-2 cursor-pointer"
            onClick={() => {
              const { email } = formRefValue.current;
              setVerifyType(type);
              setResendStatus(true);
              countDownRef.current?.sendCode(email, type);
            }}
          >
            Resend
          </span>
        </Countdown>
      </div>
    );
  };

  const _renderMoblieForm = () => {
    return (
      <Form
        name="User"
        form={form}
        initialValues={initialValues}
        autoComplete="off"
        className="w-full"
      >
        {step === 1 && (
          <>
            <Form.Item<FieldType> name="email">
              <AutoComplete
                options={options}
                defaultActiveFirstOption
                optionRender={(oriOption) => {
                  const { value } = oriOption as any;
                  const emailSuffix = value.substring(value?.indexOf("@"));

                  return (
                    <div>
                      <span className="text-bravve-sub-100">{watchEmail}</span>
                      <span className="text-bravve-100">{emailSuffix}</span>
                    </div>
                  );
                }}
                onSearch={(text) => {
                  if (text.includes("@")) {
                    setOptions([]);
                  } else {
                    setOptions(getEmails(text));
                  }
                }}
              >
                <Input
                  prefix={
                    <div className="mx-11 flex">
                      <iconpark-icon
                        icon-id="mail"
                        name="mail"
                        size="1.67rem"
                        color="#796B64"
                      />
                    </div>
                  }
                  style={{ height: 52 }}
                  allowClear={{
                    clearIcon: (
                      <iconpark-icon
                        icon-id="close-small"
                        name="close-small"
                        size="2.4rem"
                        color="#796B64"
                      />
                    ),
                  }}
                  placeholder="Enter your email address"
                />
              </AutoComplete>
            </Form.Item>

            <BravveSmartButton
              type="primary"
              htmlType="submit"
              className="mt-16 w-full"
              size="large"
              // disabled={!watchEmail}
              // 当所有数据填写完成后触发
              onSubmit={(value) => {}}
              onClick={async () => {
                await handleContinue();
              }}
            >
              Continue
            </BravveSmartButton>

            {_renderThirdLogin()}
          </>
        )}
        {(step === 2 || step === 4) && (
          <>
            <Form.Item<FieldType> name="code" style={{ marginBottom: 16 }}>
              <Input
                prefix={
                  <div className="mx-11 flex">
                    <iconpark-icon
                      icon-id="mail"
                      name="mail"
                      size="1.67rem"
                      color="#796B64"
                    />
                  </div>
                }
                style={{ height: 52 }}
                allowClear={{
                  clearIcon: (
                    <iconpark-icon
                      icon-id="close-small"
                      name="close-small"
                      size="2.4rem"
                      color="#796B64"
                    />
                  ),
                }}
                placeholder="Enter emailed code"
              />
            </Form.Item>

            <Form.Item<FieldType> name="password" style={{ marginBottom: 16 }}>
              <Input.Password
                autoComplete="new-password"
                prefix={
                  <div className="mx-11 flex">
                    <iconpark-icon
                      icon-id="keyhole"
                      name="keyhole"
                      size="1.67rem"
                      color="#796B64"
                    />
                  </div>
                }
                style={{ height: 52 }}
                iconRender={(visible) => {
                  if (!watchPassword) {
                    return null;
                  }

                  return visible ? <EyeOn width={22} /> : <EyeOff width={22} />;
                }}
                placeholder="Enter password"
              />
            </Form.Item>

            <BravveSmartButton
              type="primary"
              className="mb-16 w-full"
              size="large"
              htmlType="submit"
              // disabled={!watchCode && !watchPassword}
              onClick={async () => {
                await handleRegisterOrLogin();
              }}
            >
              {step === 2 ? "Create Account" : "Sign in"}
            </BravveSmartButton>
            {_renderResend(
              step === 2 ? VerifyType.SIGN_UP : VerifyType.RESET_PASS,
            )}
          </>
        )}
        {step === 3 && (
          <>
            <Form.Item<FieldType> name="password" style={{ marginBottom: 16 }}>
              <Input.Password
                autoComplete="new-password"
                prefix={
                  <div className="mx-11 flex">
                    <iconpark-icon
                      icon-id="keyhole"
                      name="keyhole"
                      size="1.67rem"
                      color="#796B64"
                    />
                  </div>
                }
                style={{ height: 52 }}
                iconRender={(visible) => {
                  if (!watchPassword) {
                    return null;
                  }

                  return visible ? <EyeOn width={22} /> : <EyeOff width={22} />;
                }}
                placeholder="Enter password"
              />
            </Form.Item>

            <BravveSmartButton
              type="primary"
              className="mb-16 w-full"
              size="large"
              htmlType="submit"
              // disabled={!watchPassword}
              onClick={async () => {
                await handleSignInWithPWD();
              }}
            >
              Sign in
            </BravveSmartButton>
            <div
              className="mb-16 text-center"
              onClick={async () => {
                const { email } = formRefValue.current;
                const { accountUuid, xPos } =
                  Storage.getItem("SlideVerifyInfo");

                const { status, message } =
                  xPos === ""
                    ? await smsCode({
                        email,
                        type: VerifyType.RESET_PASS,
                      })
                    : await smsCode({
                        email,
                        type: VerifyType.RESET_PASS,
                        xPos,
                        verifyType: getVerifyType(verifyType),
                        uuid: accountUuid,
                      });

                if (status === 200) {
                  messageToast.success(message);
                  setStep(4);
                  countDownRef.current?.startCountdown();
                  Storage.setItem("SlideVerifyInfo", {
                    accountUuid: "",
                    xPos: "",
                  });
                } else if (
                  status === StatusCode.TriggerRateLimit ||
                  status === StatusCode.VerificationExpired ||
                  status === StatusCode.VerificationFailed
                ) {
                  messageToast.error(message);
                  setOpenSlider(true);
                  setVerifyType(VerifyType.RESET_PASS);
                } else {
                  messageToast.error(message);
                }
              }}
            >
              <span className="cursor-pointer border-b border-bravve-100 text-center leading-22 text-bravve-100">
                Forget the password
              </span>
            </div>
            <div
              className="flex items-center justify-center"
              style={{ width: "100%" }}
            >
              <Divider style={{ minWidth: "auto", width: "100%" }} />{" "}
              <span className="mx-12 text-lg leading-24 text-bravve-sub-100">
                or
              </span>{" "}
              <Divider style={{ minWidth: "auto", width: "100%" }} />
            </div>

            <BravveSmartButton
              className="mt-16 w-full"
              size="large"
              htmlType="submit"
              onClick={async () => {
                const { email } = formRefValue.current;
                const { accountUuid, xPos } =
                  Storage.getItem("SlideVerifyInfo");

                const { status, message } =
                  xPos === ""
                    ? await smsCode({
                        email,
                        type: VerifyType.LOGIN,
                      })
                    : await smsCode({
                        email,
                        type: VerifyType.LOGIN,
                        xPos,
                        verifyType: getVerifyType(verifyType),
                        uuid: accountUuid,
                      });

                if (status === 200) {
                  messageToast.success(message);
                  setStep(5);
                  countDownRef.current?.startCountdown();
                  Storage.setItem("SlideVerifyInfo", {
                    accountUuid: "",
                    xPos: "",
                  });
                } else if (
                  status === StatusCode.TriggerRateLimit ||
                  status === StatusCode.VerificationExpired ||
                  status === StatusCode.VerificationFailed
                ) {
                  messageToast.error(message);
                  setOpenSlider(true);
                  setVerifyType(VerifyType.LOGIN);
                } else {
                  messageToast.error(message);
                }
              }}
            >
              <iconpark-icon
                icon-id="mail"
                name="mail"
                size="1.67rem"
                color="#402C22"
                style={{ position: "absolute", left: 22 }}
              />
              Sign in with code
            </BravveSmartButton>
          </>
        )}
        {step === 5 && (
          <>
            <Form.Item<FieldType>
              name="code"
              style={{
                marginBottom: 16,
                display: "flex",
                width: "100%",
                justifyContent: "space-between",
              }}
            >
              <OtpInput
                value={otp}
                onChange={setOtp}
                numInputs={6}
                shouldAutoFocus
                containerStyle={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                }}
                renderInput={(props) => {
                  return (
                    <Input
                      {...(props as any)}
                      maxLength={1}
                      style={{
                        width: 50,
                        textAlign: "center",
                        fontWeight: 500,
                        color: "#402C22",
                        fontSize: 18,
                        height: 52,
                        lineHeight: 26,
                        borderColor: "#C6C0BD",
                      }}
                    />
                  );
                }}
              />
            </Form.Item>

            <BravveSmartButton
              type="primary"
              className="mb-16 w-full"
              size="large"
              htmlType="submit"
              onClick={async () => {
                await handleSignInWithCode();
              }}
            >
              Sign in
            </BravveSmartButton>
            {_renderResend(VerifyType.LOGIN)}
            <div
              className="mt-16 flex items-center justify-center"
              style={{ width: "100%" }}
            >
              <Divider style={{ minWidth: "auto", width: "100%" }} />{" "}
              <span className="mx-12 text-lg leading-24 text-bravve-sub-100">
                or
              </span>{" "}
              <Divider style={{ minWidth: "auto", width: "100%" }} />
            </div>

            <BravveSmartButton
              className="mt-16 w-full"
              size="large"
              htmlType="submit"
              onClick={() => {
                setStep(3);
              }}
            >
              <iconpark-icon
                icon-id="mail"
                name="keyhole"
                size="1.67rem"
                color="#402C22"
                style={{ position: "absolute", left: 22 }}
              />
              Sign in with password
            </BravveSmartButton>
          </>
        )}

        {step === 6 && (
          <>
            {/* 当绑定邮箱错误时，添加红色边框 */}
            {/* validateStatus={error ? 'error': ''} */}
            <Form.Item<FieldType> name="email">
              <AutoComplete
                options={options}
                defaultActiveFirstOption
                optionRender={(oriOption) => {
                  const { value } = oriOption as any;
                  const emailSuffix = value.substring(value?.indexOf("@"));

                  return (
                    <div>
                      <span className="text-bravve-sub-100">{watchEmail}</span>
                      <span className="text-bravve-100">{emailSuffix}</span>
                    </div>
                  );
                }}
                onSearch={(text) => {
                  if (text.includes("@")) {
                    setOptions([]);
                  } else {
                    setOptions(getEmails(text));
                  }
                }}
              >
                <Input
                  prefix={
                    <div className="mx-11 flex">
                      <iconpark-icon
                        icon-id="mail"
                        name="mail"
                        size="1.67rem"
                        color="#796B64"
                      />
                    </div>
                  }
                  style={{ height: 52 }}
                  allowClear={{
                    clearIcon: (
                      <iconpark-icon
                        icon-id="close-small"
                        name="close-small"
                        size="2.4rem"
                        color="#796B64"
                      />
                    ),
                  }}
                  placeholder="Enter your email address"
                />
              </AutoComplete>
            </Form.Item>

            <BravveSmartButton
              type="primary"
              htmlType="submit"
              className="mt-16 w-full"
              size="large"
              // disabled={!watchEmail}
              // 当所有数据填写完成后触发
              onSubmit={(value) => {}}
              onClick={async () => {
                await handleVerifyEmailThirdLogin();
              }}
            >
              Sign in
            </BravveSmartButton>
          </>
        )}

        {step === 7 && (
          <>
            <Form.Item<FieldType> name="email" style={{ marginBottom: 16 }}>
              <Input
                disabled
                // defaultValue="941058545@qq.com"
                prefix={
                  <div className="mx-11 flex">
                    <iconpark-icon
                      icon-id="mail"
                      name="mail"
                      size="1.67rem"
                      color="#796B64"
                    />
                  </div>
                }
                style={{ height: 52, border: "none", boxShadow: "none" }}
                placeholder={formRefValue.current.email}
              />
            </Form.Item>

            <Form.Item<FieldType> name="password" style={{ marginBottom: 16 }}>
              <Input.Password
                autoComplete="new-password"
                prefix={
                  <div className="mx-11 flex">
                    <iconpark-icon
                      icon-id="keyhole"
                      name="keyhole"
                      size="1.67rem"
                      color="#796B64"
                    />
                  </div>
                }
                style={{ height: 52 }}
                iconRender={(visible) => {
                  if (!watchPassword) {
                    return null;
                  }

                  return visible ? <EyeOn width={22} /> : <EyeOff width={22} />;
                }}
                placeholder="Enter password"
              />
            </Form.Item>

            <BravveSmartButton
              type="primary"
              className="mb-16 w-full"
              size="large"
              htmlType="submit"
              onClick={async () => {
                const { email, password } = form.getFieldsValue();
                const { status, data, message } = await thirdUserBindEmail({
                  email,
                  authorization: btoa(`${email}:${password}`),
                  token: thirdLoginToken.current,
                  loginType: LoginType.PASSWORD,
                });

                if (status === 200) {
                  setStep(9);
                  setUserInfo(data);
                  onLoginSuccess(data.id);
                  refreshShopCart();
                } else {
                  messageToast.error(message);
                }
              }}
            >
              Confirm
            </BravveSmartButton>
            <div
              className="text-center leading-22 text-bravve-100 underline"
              onClick={() => {
                setStep(8);
              }}
            >
              Forget the password
            </div>
          </>
        )}

        {step === 8 && (
          <>
            <Form.Item<FieldType> name="code" style={{ marginBottom: 16 }}>
              <Input
                prefix={
                  <div className="mx-11 flex">
                    <iconpark-icon
                      icon-id="mail"
                      name="mail"
                      size="1.67rem"
                      color="#796B64"
                    />
                  </div>
                }
                style={{ height: 52 }}
                allowClear={{
                  clearIcon: (
                    <iconpark-icon
                      icon-id="close-small"
                      name="close-small"
                      size="2.4rem"
                      color="#796B64"
                    />
                  ),
                }}
                placeholder="Enter emailed code"
              />
            </Form.Item>

            <Form.Item<FieldType> name="password" style={{ marginBottom: 16 }}>
              <Input.Password
                autoComplete="new-password"
                prefix={
                  <div className="mx-11 flex">
                    <iconpark-icon
                      icon-id="keyhole"
                      name="keyhole"
                      size="1.67rem"
                      color="#796B64"
                    />
                  </div>
                }
                style={{ height: 52 }}
                iconRender={(visible) => {
                  if (!watchPassword) {
                    return null;
                  }

                  return visible ? <EyeOn width={22} /> : <EyeOff width={22} />;
                }}
                placeholder="Enter password"
              />
            </Form.Item>

            <BravveSmartButton
              type="primary"
              className="mb-16 w-full"
              size="large"
              htmlType="submit"
              // disabled={!watchCode && !watchPassword}
              onClick={async () => {
                await handleRegisterOrLogin();
              }}
            >
              Sign in
            </BravveSmartButton>
            {_renderResend(VerifyType.RESET_PASS)}
          </>
        )}

        {step === 9 && (
          <BravveSmartButton
            type="primary"
            className="w-full"
            size="large"
            htmlType="submit"
            onClick={async () => {
              setStep(1);
              onClose();
              setThirdLogin(false);
            }}
          >
            Confirm
          </BravveSmartButton>
        )}
      </Form>
    );
  };

  const _renderBottom = () => {
    if (step === 7 || step === 8 || step === 9) {
      return null;
    }

    return (
      <div>
        <div className="text-center text-sm leading-22 text-bravve-sub-100">
          By continuing, you agree to our
        </div>
        <div className="text-center text-sm leading-22 text-bravve-sub-100">
          <div
            className="inline-block cursor-pointer border-b border-solid border-bravve-sub-100"
            onClick={() =>
              openPricacyPolicy(PrivacyPolicyPagesEnum.PrivacyPolicy)
            }
          >
            Privacy Policy
          </div>{" "}
          &{" "}
          <span
            className="inline-block cursor-pointer border-b border-solid border-bravve-sub-100"
            onClick={() =>
              openPricacyPolicy(PrivacyPolicyPagesEnum.TermsofService)
            }
          >
            Terms of Service.
          </span>
        </div>
      </div>
    );
  };

  const _renderPCLeft = () => {
    return (
      <div className="loginBgModal w-1/2 border-r">
        <div className="mt-52">
          <Image
            src={BravvePc}
            className="m-auto"
            width={130}
            height={29}
            alt={"Bravve"}
          />
        </div>
        <div>
          {carouselArr.map(({ icon, title, des }, index) => (
            <div
              key={title}
              className="mb-58 flex w-full pl-45 first:mt-80 last:mb-69"
            >
              {icon}
              <div className="ml-24 flex flex-col justify-between">
                <div className={`text-xl font-medium leading-26 text-white`}>
                  {title}
                </div>
                <div className="mt-2 leading-22 text-bravve-300">{des}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const _renderPCTop = () => {
    return (
      <div>
        {step === 1 && (
          <div className="mb-32">
            <div className="text-center text-2xl font-bold leading-28 text-bravve-100">
              Sign in / Register
            </div>
            <div className="mt-16 flex items-center justify-center text-success-200">
              <KeyHole width={15} fill={"#24961B"} />
              <span className="ml-6 leading-22">
                All data will be encrypted
              </span>
            </div>
          </div>
        )}
        {(step === 2 || step === 3 || step === 5) && (
          <div className="mb-28">
            <div className="text-center text-2xl font-bold leading-28 text-bravve-100">
              {step === 2 ? "Create Account" : "Sign in"}
            </div>
            <div className="mt-16 text-center leading-22 text-bravve-100">
              {formRefValue.current.email}
            </div>
            <div
              className="mt-6 cursor-pointer text-center text-bravve-100"
              onClick={() => {
                setStep(1);
              }}
            >
              <span className="border-b border-bravve-100 leading-22">
                Use a different account
              </span>
            </div>
          </div>
        )}
        {step === 4 && (
          <div className="mb-30">
            <div className="text-center text-2xl font-bold leading-28 text-bravve-100">
              Forgot Password
            </div>
            <div className="mt-16 text-center leading-22 text-bravve-100">
              We&apos;ve sent a code to {formRefValue.current.email}.
            </div>
            <div className="text-center leading-22 text-bravve-100">
              Please enter the code below.
            </div>
          </div>
        )}
        {step === 6 && (
          <div className="mb-32">
            <div className="text-center text-2xl font-bold leading-28 text-bravve-100">
              Link Account to Email
            </div>
            <div className="mt-16 text-center leading-22 text-bravve-100">
              Your account needs to be linked to
            </div>
            <div className="text-center leading-22 text-bravve-100">
              an email address
            </div>
          </div>
        )}
        {step === 7 && (
          <div className="mb-23">
            <div className="text-center text-2xl font-bold leading-28 text-bravve-100">
              Sign in
            </div>
            <div className="mt-16 text-center leading-22 text-bravve-100">
              Your email address has already been registered.
            </div>
            <div className="text-center leading-22 text-bravve-100">
              Pleaselog in with the existing account.
            </div>
          </div>
        )}
        {step === 8 && (
          <div className="mb-30">
            <div className="text-center text-2xl font-bold leading-28 text-bravve-100">
              Forgot Password
            </div>
            <div className="mt-16 text-center leading-22 text-bravve-100">
              We&apos;ve sent a code to {formRefValue.current.email}.
            </div>
            <div className="text-center leading-22 text-bravve-100">
              Please enter the code below.
            </div>
          </div>
        )}
        {step === 9 && (
          <div className="mb-40 text-center text-bravve-100">
            <div className="flex items-center justify-center">
              <Succeed width="56" />
            </div>
            <div className="my-16 text-2xl font-bold leading-28">
              Login Successful
            </div>
            <div className="leading-22">
              Your account is now linked to{" "}
              {isBindGoogle ? ThirdLogin.Google : ThirdLogin.Facebook}.
            </div>
            <div className="leading-22">
              In future, you can sign in using{" "}
              {isBindGoogle ? ThirdLogin.Google : ThirdLogin.Facebook} or
            </div>
            <div className="leading-22">your email and password.</div>
          </div>
        )}
      </div>
    );
  };

  const _renderPCForm = () => {
    return (
      <Form
        name="User"
        form={form}
        initialValues={initialValues}
        autoComplete="off"
        className="w-full"
      >
        {step === 1 && (
          <>
            <Form.Item<FieldType> name="email">
              <AutoComplete
                options={options}
                defaultActiveFirstOption
                optionRender={(oriOption) => {
                  const { value } = oriOption as any;
                  const emailSuffix = value.substring(value?.indexOf("@"));

                  return (
                    <div>
                      <span className="text-bravve-sub-100">{watchEmail}</span>
                      <span className="text-bravve-100">{emailSuffix}</span>
                    </div>
                  );
                }}
                onSearch={(text) => {
                  if (text.includes("@")) {
                    setOptions([]);
                  } else {
                    setOptions(getEmails(text));
                  }
                }}
              >
                <Input
                  prefix={
                    <div className="mx-11 flex">
                      <iconpark-icon
                        name="mail"
                        icon-id="mail"
                        size="1.67rem"
                        color="#796B64"
                      />
                    </div>
                  }
                  style={{ height: 52 }}
                  allowClear={{
                    clearIcon: (
                      <iconpark-icon
                        icon-id="close-small"
                        name="close-small"
                        size="2.4rem"
                        color="#796B64"
                      />
                    ),
                  }}
                  placeholder="Enter your email address"
                />
              </AutoComplete>
            </Form.Item>

            <BravveSmartButton
              type="primary"
              className="mt-16 w-full"
              htmlType="submit"
              size="large"
              // disabled={!watchEmail}
              onClick={async () => {
                await handleContinue();
              }}
            >
              Continue
            </BravveSmartButton>

            {_renderThirdLogin()}
          </>
        )}
        {(step === 2 || step === 4) && (
          <>
            <Form.Item<FieldType> name="code" style={{ marginBottom: 16 }}>
              <Input
                prefix={
                  <div className="mx-11 flex">
                    <iconpark-icon
                      icon-id="mail"
                      name="mail"
                      size="1.67rem"
                      color="#796B64"
                    />
                  </div>
                }
                style={{ height: 52 }}
                allowClear={{
                  clearIcon: (
                    <iconpark-icon
                      icon-id="close-small"
                      name="close-small"
                      size="2.4rem"
                      color="#796B64"
                    />
                  ),
                }}
                placeholder="Enter emailed code"
              />
            </Form.Item>

            <Form.Item<FieldType> name="password" style={{ marginBottom: 16 }}>
              <Input.Password
                autoComplete="new-password"
                prefix={
                  <div className="mx-11 flex">
                    <iconpark-icon
                      icon-id="keyhole"
                      name="keyhole"
                      size="1.67rem"
                      color="#796B64"
                    />
                  </div>
                }
                style={{ height: 52 }}
                iconRender={(visible) => {
                  if (!watchPassword) {
                    return null;
                  }

                  return visible ? <EyeOn width={22} /> : <EyeOff width={22} />;
                }}
                placeholder="Enter password"
              />
            </Form.Item>

            <BravveSmartButton
              type="primary"
              className="mb-16 w-full"
              size="large"
              htmlType="submit"
              // disabled={!watchCode && !watchPassword}
              onClick={async () => {
                await handleRegisterOrLogin();
              }}
            >
              {step === 2 ? "Create Account" : "Sign in"}
            </BravveSmartButton>
            {_renderResend(
              step === 2 ? VerifyType.SIGN_UP : VerifyType.RESET_PASS,
            )}
          </>
        )}
        {step === 3 && (
          <>
            <Form.Item<FieldType> name="password" style={{ marginBottom: 16 }}>
              <Input.Password
                autoComplete="new-password"
                prefix={
                  <div className="mx-11 flex">
                    <iconpark-icon
                      icon-id="keyhole"
                      name="keyhole"
                      size="1.67rem"
                      color="#796B64"
                    />
                  </div>
                }
                style={{ height: 52 }}
                iconRender={(visible) => {
                  if (!watchPassword) {
                    return null;
                  }

                  return visible ? <EyeOn width={22} /> : <EyeOff width={22} />;
                }}
                placeholder="Enter password"
              />
            </Form.Item>

            <BravveSmartButton
              type="primary"
              className="mb-16 w-full"
              size="large"
              htmlType="submit"
              // disabled={!watchPassword}
              onClick={async () => {
                await handleSignInWithPWD();
              }}
            >
              Sign in
            </BravveSmartButton>
            <div
              className="mb-16 text-center"
              onClick={async () => {
                const { email } = formRefValue.current;
                const { accountUuid, xPos } =
                  Storage.getItem("SlideVerifyInfo");

                const { status, message } =
                  xPos === ""
                    ? await smsCode({
                        email,
                        type: VerifyType.RESET_PASS,
                      })
                    : await smsCode({
                        email,
                        type: VerifyType.RESET_PASS,
                        xPos,
                        verifyType: getVerifyType(verifyType),
                        uuid: accountUuid,
                      });

                if (status === 200) {
                  messageToast.success(message);
                  setStep(4);
                  countDownRef.current?.startCountdown();
                  Storage.setItem("SlideVerifyInfo", {
                    accountUuid: "",
                    xPos: "",
                  });
                } else if (
                  status === StatusCode.TriggerRateLimit ||
                  status === StatusCode.VerificationExpired ||
                  status === StatusCode.VerificationFailed
                ) {
                  messageToast.error(message);
                  setOpenSlider(true);
                  setVerifyType(VerifyType.RESET_PASS);
                } else {
                  messageToast.error(message);
                }
              }}
            >
              <span className="cursor-pointer border-b border-bravve-100 text-center leading-22 text-bravve-100">
                Forget the password
              </span>
            </div>
            <div
              className="flex items-center justify-center"
              style={{ width: "100%" }}
            >
              <Divider style={{ minWidth: "auto", width: "100%" }} />{" "}
              <span className="mx-12 text-lg leading-24 text-bravve-sub-100">
                or
              </span>{" "}
              <Divider style={{ minWidth: "auto", width: "100%" }} />
            </div>

            <BravveSmartButton
              className="mt-16 w-full"
              size="large"
              htmlType="submit"
              onClick={async () => {
                const { email } = formRefValue.current;
                const { accountUuid, xPos } =
                  Storage.getItem("SlideVerifyInfo");

                const { status, message } =
                  xPos === ""
                    ? await smsCode({
                        email,
                        type: VerifyType.LOGIN,
                      })
                    : await smsCode({
                        email,
                        type: VerifyType.LOGIN,
                        xPos,
                        verifyType: getVerifyType(verifyType),
                        uuid: accountUuid,
                      });

                if (status === 200) {
                  messageToast.success(message);
                  setStep(5);
                  countDownRef.current?.startCountdown();
                  Storage.setItem("SlideVerifyInfo", {
                    accountUuid: "",
                    xPos: "",
                  });
                } else if (
                  status === StatusCode.TriggerRateLimit ||
                  status === StatusCode.VerificationExpired ||
                  status === StatusCode.VerificationFailed
                ) {
                  messageToast.error(message);
                  setOpenSlider(true);
                  setVerifyType(VerifyType.LOGIN);
                } else {
                  messageToast.error(message);
                }
              }}
            >
              <iconpark-icon
                icon-id="mail"
                name="mail"
                size="1.67rem"
                color="#402C22"
                style={{ position: "absolute", left: 22 }}
              />
              Sign in with code
            </BravveSmartButton>
          </>
        )}
        {step === 5 && (
          <>
            <Form.Item<FieldType>
              name="code"
              style={{
                marginBottom: 16,
                display: "flex",
                width: "100%",
                justifyContent: "space-between",
              }}
            >
              <OtpInput
                value={otp}
                onChange={setOtp}
                numInputs={6}
                shouldAutoFocus
                containerStyle={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                }}
                renderInput={(props) => {
                  return (
                    <Input
                      {...(props as any)}
                      maxLength={1}
                      style={{
                        width: 50,
                        textAlign: "center",
                        fontWeight: 500,
                        color: "#402C22",
                        fontSize: 18,
                        height: 52,
                        lineHeight: 26,
                        borderColor: "#C6C0BD",
                      }}
                    />
                  );
                }}
              />
            </Form.Item>

            <BravveSmartButton
              type="primary"
              className="mb-16 w-full"
              size="large"
              htmlType="submit"
              onClick={async () => {
                await handleSignInWithCode();
              }}
            >
              Sign in
            </BravveSmartButton>
            {_renderResend(VerifyType.LOGIN)}
            <div
              className="flex items-center justify-center py-16"
              style={{ width: "100%" }}
            >
              <Divider style={{ minWidth: "auto", width: "100%" }} />{" "}
              <span className="mx-12 text-lg leading-24 text-bravve-sub-100">
                or
              </span>{" "}
              <Divider style={{ minWidth: "auto", width: "100%" }} />
            </div>

            <BravveSmartButton
              className="mb-16 w-full"
              size="large"
              htmlType="submit"
              onClick={() => {
                setStep(3);
              }}
            >
              <iconpark-icon
                icon-id="keyhole"
                name="keyhole"
                size="1.67rem"
                color="#402C22"
                style={{ position: "absolute", left: 22 }}
              />
              Sign in with password
            </BravveSmartButton>
          </>
        )}

        {step === 6 && (
          <div className="mb-110">
            <Form.Item<FieldType> name="email">
              <AutoComplete
                options={options}
                defaultActiveFirstOption
                optionRender={(oriOption) => {
                  const { value } = oriOption as any;
                  const emailSuffix = value.substring(value?.indexOf("@"));

                  return (
                    <div>
                      <span className="text-bravve-sub-100">{watchEmail}</span>
                      <span className="text-bravve-100">{emailSuffix}</span>
                    </div>
                  );
                }}
                onSearch={(text) => {
                  if (text.includes("@")) {
                    setOptions([]);
                  } else {
                    setOptions(getEmails(text));
                  }
                }}
              >
                <Input
                  prefix={
                    <div className="mx-11 flex">
                      <iconpark-icon
                        name="mail"
                        icon-id="mail"
                        size="1.67rem"
                        color="#796B64"
                      />
                    </div>
                  }
                  style={{ height: 52 }}
                  allowClear={{
                    clearIcon: (
                      <iconpark-icon
                        icon-id="close-small"
                        name="close-small"
                        size="2.4rem"
                        color="#796B64"
                      />
                    ),
                  }}
                  placeholder="Enter your email address"
                />
              </AutoComplete>
            </Form.Item>

            <BravveSmartButton
              type="primary"
              className="mt-16 w-full"
              htmlType="submit"
              size="large"
              // disabled={!watchEmail}
              onClick={async () => {
                await handleVerifyEmailThirdLogin();
              }}
            >
              Sign in
            </BravveSmartButton>
          </div>
        )}

        {step === 7 && (
          <>
            <Form.Item<FieldType> name="email" style={{ marginBottom: 16 }}>
              <Input
                disabled
                // defaultValue="941058545@qq.com"
                prefix={
                  <div className="mx-11 flex">
                    <iconpark-icon
                      icon-id="mail"
                      name="mail"
                      size="1.67rem"
                      color="#796B64"
                    />
                  </div>
                }
                style={{ height: 52, border: "none", boxShadow: "none" }}
                placeholder={formRefValue.current.email}
              />
            </Form.Item>

            <Form.Item<FieldType> name="password" style={{ marginBottom: 16 }}>
              <Input.Password
                autoComplete="new-password"
                prefix={
                  <div className="mx-11 flex">
                    <iconpark-icon
                      icon-id="keyhole"
                      name="keyhole"
                      size="1.67rem"
                      color="#796B64"
                    />
                  </div>
                }
                style={{ height: 52 }}
                iconRender={(visible) => {
                  if (!watchPassword) {
                    return null;
                  }

                  return visible ? <EyeOn width={22} /> : <EyeOff width={22} />;
                }}
                placeholder="Enter password"
              />
            </Form.Item>

            <BravveSmartButton
              type="primary"
              className="mb-16 w-full"
              size="large"
              htmlType="submit"
              onClick={async () => {
                const { email, password } = form.getFieldsValue();
                const { status, data, message } = await thirdUserBindEmail({
                  email,
                  authorization: btoa(`${email}:${password}`),
                  token: thirdLoginToken.current,
                  loginType: LoginType.PASSWORD,
                });

                if (status === 200) {
                  setStep(9);
                  setUserInfo(data);
                  onLoginSuccess(data.id);
                  refreshShopCart();
                } else {
                  messageToast.error(message);
                }
              }}
            >
              Confirm
            </BravveSmartButton>
            <div
              className="mb-55 cursor-pointer border-bravve-100 text-center leading-22 text-bravve-100 underline"
              onClick={() => {
                setStep(8);
              }}
            >
              Forget the password
            </div>
          </>
        )}

        {step === 8 && (
          <>
            <Form.Item<FieldType> name="code" style={{ marginBottom: 16 }}>
              <Input
                prefix={
                  <div className="mx-11 flex">
                    <iconpark-icon
                      icon-id="mail"
                      name="mail"
                      size="1.67rem"
                      color="#796B64"
                    />
                  </div>
                }
                style={{ height: 52 }}
                allowClear={{
                  clearIcon: (
                    <iconpark-icon
                      icon-id="close-small"
                      name="close-small"
                      size="2.4rem"
                      color="#796B64"
                    />
                  ),
                }}
                placeholder="Enter emailed code"
              />
            </Form.Item>

            <Form.Item<FieldType> name="password" style={{ marginBottom: 16 }}>
              <Input.Password
                autoComplete="new-password"
                prefix={
                  <div className="mx-11 flex">
                    <iconpark-icon
                      icon-id="keyhole"
                      name="keyhole"
                      size="1.67rem"
                      color="#796B64"
                    />
                  </div>
                }
                style={{ height: 52 }}
                iconRender={(visible) => {
                  if (!watchPassword) {
                    return null;
                  }

                  return visible ? <EyeOn width={22} /> : <EyeOff width={22} />;
                }}
                placeholder="Enter password"
              />
            </Form.Item>

            <BravveSmartButton
              type="primary"
              className="mb-16 w-full"
              size="large"
              htmlType="submit"
              // disabled={!watchCode && !watchPassword}
              onClick={async () => {
                await handleRegisterOrLogin();
              }}
            >
              Sign in
            </BravveSmartButton>
            <div className="mb-44">{_renderResend(VerifyType.RESET_PASS)}</div>
          </>
        )}

        {step === 9 && (
          <BravveSmartButton
            type="primary"
            className="mb-44 w-full"
            size="large"
            htmlType="submit"
            onClick={async () => {
              setStep(1);
              onClose();
              setThirdLogin(false);
            }}
          >
            Confirm
          </BravveSmartButton>
        )}
      </Form>
    );
  };

  const _renderPCRight = () => {
    return (
      <div className={`${isThirdLogin ? "w-full" : "w-1/2"} flex flex-col`}>
        <div
          className="mr-12 mt-12 flex cursor-pointer justify-end"
          onClick={() => {
            handleGta();
            setThirdLogin(false);
            setStep(1);
            form.resetFields();
            onClose();
          }}
        >
          <iconpark-icon
            name="close-small"
            size="3rem"
            icon-id="close-small"
            color="#796B64"
          />
        </div>
        {_renderPCTop()}
        <div className="flex flex-1 flex-col justify-between px-30 pb-25">
          {_renderPCForm()}
          {_renderBottom()}
        </div>
      </div>
    );
  };

  return (
    <>
      {isMobile ? (
        <Drawer
          className="userLoginDrawer"
          title={
            <Image
              src={Bravve}
              style={{ marginLeft: "calc(50% - 80px)" }}
              className="m-auto"
              width={130}
              height={29}
              alt={"Bravve"}
            />
          }
          open={open}
          onClose={() => {
            handleGta();
            setThirdLogin(false);
            setStep(1);
            form.resetFields();
            onClose();
          }}
          closeIcon={<CloseSmall theme="filled" fill="#402C22" size={"3rem"} />}
          placement={"bottom"}
          height={"100%"}
          styles={{
            header: {
              paddingRight: isMobile ? "1.6rem" : "1.6rem",
              paddingLeft: isMobile ? "1.6rem" : "1.6rem",
              height: 52,
            },
            body: {
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              alignItems: "center",
              padding: isMobile ? "3.5rem 1.6rem 2rem" : "3.5rem 1.6rem 2rem",
            },
          }}
        >
          <div className="flex w-full flex-col items-center">
            {_renderMoblieTop()}
            {_renderMoblieForm()}
          </div>
          <div className="w-full">
            {!isThirdLogin && _renderMoblieCarousel()}
            {_renderBottom()}
          </div>
        </Drawer>
      ) : (
        <Modal
          open={open}
          className="userLoginModal"
          width={`${isThirdLogin ? 420 : 840}`}
          centered
          footer={null}
          maskClosable
          closeIcon={null}
          onCancel={() => {
            handleGta();
            setThirdLogin(false);
            setStep(1);
            form.resetFields();
            onClose();
          }}
        >
          <div
            className="flex flex-row"
            style={{ width: isThirdLogin ? 420 : 840 }}
          >
            {!isThirdLogin && _renderPCLeft()}
            {_renderPCRight()}
          </div>
        </Modal>
      )}
      <SliderVerification
        open={openSlider}
        email={watchEmail || formRefValue.current.email}
        step={step}
        password={watchPassword}
        onLoginWithPWDSuccess={(data: any) => {
          setOpenSlider(false);
          handlePWDSuccess(data);
        }}
        type={verifyType}
        onClose={() => {
          setOpenSlider(false);
        }}
        onSendSuccessClose={(type: VerifyType | undefined) => {
          setOpenSlider(false);

          // 这个是根据，后端提示“验证码已发送，请稍后再试”错误后，前端需要手动跳转到下一页面
          if (type === VerifyType.SIGN_UP) {
            setStep(2);
          } else if (type === VerifyType.RESET_PASS) {
            setStep(4);
          } else if (type === VerifyType.LOGIN) {
            setStep(5);
          }

          // 滑块验证后还需要调用计时器倒计时
          if (resendStatus) {
            countDownRef.current?.startCountdown();
            setResendStatus(false);
          }
        }}
        verifyAction={() => {
          if (verifyType === VerifyType.SIGN_UP) {
            setStep(2);
          } else if (verifyType === VerifyType.RESET_PASS) {
            setStep(4);
          } else if (verifyType === VerifyType.LOGIN) {
            setStep(5);
          }
        }}
      />
      <CouponCom
        open={openCoupon}
        onClose={() => {
          setOpenCoupon(false);
        }}
      />
    </>
  );
};
